import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AnalyticsService } from "@gen/tracker/v1/analytics_connect";
import type { DrilldownRow } from "@gen/tracker/v1/analytics_pb";
import type { StatsRow } from "@gen/tracker/v1/reporting_pb";
import { DimensionFilter, DimensionFilterOp } from "@gen/tracker/v1/reporting_pb";
import { DateRange, Pagination } from "@gen/tracker/v1/common_pb";

import { createClient } from "@/lib/connect";
import type { ConversionBasis } from "@/lib/period";
import { basisToProto } from "@/lib/services/analytics-service";

const client = createClient(AnalyticsService);

export interface CampaignReportParams {
  campaignId: string;
  from: string;
  to: string;
  tz: string;
  basis: ConversionBasis;
  /** Reporting-schema dimension keys, outermost first (1..4). */
  dims: string[];
  /** dimension key → values the rows must carry (IN). */
  filters?: Record<string, string[]>;
  /** Parent rows counted by the server, in tree order. */
  subtotals?: boolean;
  compare?: { from: string; to: string };
}

export interface CampaignReport {
  rows: DrilldownRow[];
  /** How many top-level groups the slice has — more than the rows when cut. */
  total: number;
  totals: StatsRow | undefined;
  compareTotals: StatsRow | undefined;
}

/**
 * One campaign split by the reporting catalog's dimensions — the card's
 * «Отчёт», its money curve and the «Что выделяется» checks. The same
 * GetBreakdownDrilldown every report reads, so a number on the card is the
 * number in «Отчёты» for the same slice.
 */
export function useCampaignReport(p: CampaignReportParams, enabled = true) {
  return useQuery({
    queryKey: ["campaign-report", p] as const,
    enabled: enabled && !!p.campaignId && p.dims.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<CampaignReport> => {
      const res = await client.getBreakdownDrilldown({
        streamIds: [p.campaignId],
        dateRange: new DateRange({ from: p.from, to: p.to, timezone: p.tz }),
        dimensionKeys: p.dims,
        attributionBasis: basisToProto(p.basis),
        pagination: new Pagination({ limit: 2000, offset: 0 }),
        withSubtotals: !!p.subtotals,
        dimensionFilters: Object.entries(p.filters ?? {})
          .filter(([, values]) => values.length > 0)
          .map(([dimensionKey, values]) => new DimensionFilter({ dimensionKey, op: DimensionFilterOp.IN, values })),
        compareRange: p.compare ? new DateRange({ from: p.compare.from, to: p.compare.to, timezone: p.tz }) : undefined,
      });
      return { rows: res.rows, total: res.pagination?.total ?? res.rows.length, totals: res.totals, compareTotals: res.compareTotals };
    },
  });
}
